/* eslint-disable react/jsx-no-literals */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';

import BurgerIcon from '@/assets/icons/burger.svg';
import BurgerHoveredIcon from '@/assets/icons/burger-hovered.svg';
import CloseSquaredIcon from '@/assets/icons/close-squared.svg';
import { CANVAS_MODE_BUTTON_HEIGHT, CANVAS_MODE_BUTTON_WIDTH } from '@/features/canvas/constants';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';

interface ViewOption {
  isGantt: boolean;
  isSwimlane: boolean;
  key: string;
  label: string;
}

const viewOptions: ViewOption[] = [
  { key: 'sunray', label: 'Sunray', isSwimlane: false, isGantt: false },
  { key: 'swimlane', label: 'Swimlane', isSwimlane: true, isGantt: false },
  { key: 'timeline', label: 'Timeline', isSwimlane: false, isGantt: true },
];

const SwimlaneViewButton = () => {
  const {
    isSwimlaneView,
    setIsSwimlaneView,
    isGanttView,
    setIsGanttView,
    isPreview,
    isPublicView,
  } = useEditorContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ left: number; top: number } | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const getCurrentView = (): ViewOption => {
    if (isGanttView) return viewOptions[2]; // Timeline
    if (isSwimlaneView) return viewOptions[1]; // Swimlane
    return viewOptions[0]; // Sunray
  };

  const getButtonIcon = () => {
    if (isDropdownOpen) return CloseSquaredIcon;
    if (isHovered) return BurgerHoveredIcon;
    return BurgerIcon;
  };

  const handleViewSelect = (option: ViewOption) => {
    setIsSwimlaneView(option.isSwimlane);
    setIsGanttView(option.isGantt);
    setIsDropdownOpen(false);
    setDropdownPosition(null);
  };

  const handleButtonClick = () => {
    if (!isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is outside both the button and the dropdown menu
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsideDropdown = !target.closest('[data-dropdown-menu]');

      if (isOutsideButton && isOutsideDropdown) {
        setIsDropdownOpen(false);
        setDropdownPosition(null);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Only show burger menu in preview or public view modes
  if (!isPreview && !isPublicView) {
    return null;
  }

  return (
    <>
      <ToggleContainer ref={dropdownRef}>
        <BurgerButton
          ref={buttonRef}
          onClick={handleButtonClick}
          $isOpen={isDropdownOpen}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <StyledSVG src={getButtonIcon()} />
        </BurgerButton>
      </ToggleContainer>

      {isDropdownOpen &&
        dropdownPosition &&
        createPortal(
          <DropdownMenu
            data-dropdown-menu
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            {viewOptions.map(option => {
              const isSelected = getCurrentView().key === option.key;
              return (
                <DropdownItem
                  key={option.key}
                  onClick={() => handleViewSelect(option)}
                  $isSelected={isSelected}
                >
                  {option.label}
                </DropdownItem>
              );
            })}
          </DropdownMenu>,
          document.body
        )}
    </>
  );
};

const ToggleContainer = styled.div`
  position: sticky;
  width: ${CANVAS_MODE_BUTTON_WIDTH}px;
  height: ${CANVAS_MODE_BUTTON_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const BurgerButton = styled.button<{ $isOpen: boolean }>`
  background: ${({ theme }) => theme.palette.brand.white};
  border: none;
  border-radius: 5px;
  padding: 5px;
  width: ${CANVAS_MODE_BUTTON_WIDTH}px;
  height: ${CANVAS_MODE_BUTTON_HEIGHT}px;
  cursor: pointer;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.palette.brand.backgroundSidebar};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const StyledSVG = styled(SVG)`
  width: 20px;
  height: 20px;
`;

const DropdownMenu = styled.div`
  position: fixed;
  background: ${({ theme }) => theme.palette.brand.white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  border-radius: 8px;
  overflow: hidden;
  z-index: 10000;
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  font-weight: ${({ $isSelected }) => ($isSelected ? '700' : '400')};
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.brand.backgroundSidebar};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.palette.brand.secondary}40;
  }
`;

export { SwimlaneViewButton };
